import { Navigate, Route, Routes } from 'react-router-dom';
import ContentApprovalsPage from './pages/ContentApprovalsPage';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Reset from './pages/Reset';
import Setup from './pages/Setup';
import WhatsNew from './pages/WhatsNew';
import UserManual from './pages/UserManual';
import MiabSurveyPage from './pages/MiabSurveyPage';
import MiabAdminPage from './pages/MiabAdminPage';
import MiabOctoberAnnouncement from './pages/MiabOctoberAnnouncement';
import NpsPage from './pages/NpsPage';
import ChatRolloutPage from './pages/ChatRolloutPage';
import DmrPage from './pages/DmrPage';
import DmrCallPage from './pages/DmrCallPage';
import NpsRespond from './pages/NpsRespond';
import ChatMagicPage from './pages/ChatMagicPage';
import ImpersonationBanner from './components/ImpersonationBanner';
import ApiCreditBanner from './components/ApiCreditBanner';
import ToastContainer from './components/Toast';
import { useAuth } from './hooks/useAuth';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <span>Loading…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      <ImpersonationBanner />
      <ApiCreditBanner />
      {children}
    </>
  );
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <span>Loading…</span>
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <Login />
            </PublicOnlyRoute>
          }
        />
        <Route path="/setup" element={<Setup />} />
        <Route path="/reset" element={<Reset />} />
        {/* Public NPS respondent page — no login; gated by the opaque link token. */}
        <Route path="/nps/r/:token" element={<NpsRespond />} />
        {/* Public chat magic link — no login; one conversation, device-bound, 8h (Flow-Spec §6 #5). */}
        <Route path="/chat/m/:token" element={<ChatMagicPage />} />
        <Route
          path="/content-approvals"
          element={
            <ProtectedRoute>
              <ContentApprovalsPage />
            </ProtectedRoute>
          }
        />
        {/* Admin survey management + live results. Declared before /miab/:slug
            so the literal "admin" segment wins over the slug pattern. */}
        <Route
          path="/miab/admin"
          element={
            <ProtectedRoute>
              <MiabAdminPage />
            </ProtectedRoute>
          }
        />
        {/* /miab shows whatever is open; /miab/<slug> is the stable link that
            goes in the announcement email. */}
        <Route
          path="/miab"
          element={
            <ProtectedRoute>
              <MiabSurveyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/miab/:slug"
          element={
            <ProtectedRoute>
              <MiabSurveyPage />
            </ProtectedRoute>
          }
        />
        {/* Marketing announcements. One route per announcement — the banner
            that points here retires itself, the page stays as the record. */}
        <Route
          path="/announcements/october-2026-miab"
          element={
            <ProtectedRoute>
              <MiabOctoberAnnouncement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/nps"
          element={
            <ProtectedRoute>
              <NpsPage />
            </ProtectedRoute>
          }
        />
        {/* DMR call scheduling (#216). Open to every role; the API decides who
            can use the season (admin → FBC → everyone) and scopes the data. */}
        <Route
          path="/dmr"
          element={
            <ProtectedRoute>
              <DmrPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dmr/call/:id"
          element={
            <ProtectedRoute>
              <DmrCallPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat-rollout"
          element={
            <ProtectedRoute>
              <ChatRolloutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whats-new"
          element={
            <ProtectedRoute>
              <WhatsNew />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-manual"
          element={
            <ProtectedRoute>
              <UserManual />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
      <ToastContainer />
    </>
  );
}
